import React, { useEffect, useState } from "react";

import { CopilotModalShell } from "../CopilotModalShell";
import { ReviewForm } from "./recording/ReviewForm";
import { getWorkflow, updateWorkflow, createWorkflow } from "./workflowsApi";

/**
 * Edit a workflow you own, or save a modified copy (clone) of any workflow you
 * can see. Reuses ReviewForm as the editor. `mode` is "edit" | "clone".
 *   - edit  → PATCH /workflows/{id}
 *   - clone → POST  /workflows  (new private copy, name prefilled "Copy of …")
 * onSaved(savedWorkflow) fires after a successful save; onClose dismisses.
 */
export function WorkflowEditModal({ workflowId, mode, onClose, onSaved }) {
  const [playbook, setPlaybook] = useState(null);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getWorkflow(workflowId)
      .then((w) => {
        if (cancelled) return;
        setPlaybook({
          name: mode === "clone" ? `Copy of ${w.name}` : w.name,
          summary: w.summary || "",
          prerequisites: w.prerequisites || [],
          parameters: w.parameters || [],
          steps: w.steps || [],
          notes: w.notes || "",
        });
      })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [workflowId, mode]);

  const onSave = async ({ name, summary, playbookOverride }) => {
    setError(null);
    try {
      const saved = mode === "edit"
        ? await updateWorkflow(workflowId, { name, summary, playbook: playbookOverride })
        : await createWorkflow({ name, summary, playbook: playbookOverride });
      if (onSaved) onSaved(saved);
    } catch (e) {
      setError({ code: e.code || "unknown", message: e.message_text || e.message || "Save failed" });
    }
  };

  const title = mode === "clone" ? "Save a copy" : "Edit workflow";
  const saveLabel = mode === "clone" ? "Save a copy" : "Save changes";

  return (
    <CopilotModalShell title={title} onClose={onClose} closeOnBackdrop={false}>
      {!playbook && !loadError && <div className="copilot-modal-body">Loading…</div>}
      {loadError && <div className="copilot-modal-body">Couldn't load this workflow.</div>}
      {playbook && (
        <ReviewForm
          playbook={playbook}
          error={error}
          onSave={onSave}
          onCancel={onClose}
          saveLabel={saveLabel}
        />
      )}
    </CopilotModalShell>
  );
}
